import { useEffect, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { FileTypeId, Nullish } from '@witmetrics/utils';
import type { LoadedFile } from '@/providers/LoadedFilesProvider';
import { useScene } from '@/providers/SceneProvider';
import { centerObjects } from '@/utils/scans';
import {
  getLoadedImage,
  getLoadedPdf,
  getLoadedScan,
  getLoadedSweethSequence,
  getLoadedVideo,
} from '@/utils/files';
import Scene from '@/components/Scene';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import ImageViewer from '@/components/viewers/ImageViewer';
import PdfViewer from '@/components/viewers/PdfViewer';
import VideoViewer from '@/components/viewers/VideoViewer';
import SweethSequenceViewer from '@/components/viewers/SweethSequenceViewer';
import { buildLoadedFiles, classes } from './utils';

export type LoadedFileViewerProps = {
  isLoading?: boolean;
  floatingHeader?: boolean;
  sceneClassName?: string;
  file?:
    | {
        id: number;
        name: string;
        fileTypeId?: FileTypeId | Nullish;
      }
    | Nullish;
  loadedFile?: LoadedFile | Nullish;
  unsupportedFallback?: ReactNode;
};

export default function LoadedFileViewer({
  isLoading = false,
  floatingHeader = false,
  sceneClassName,
  file,
  loadedFile,
  unsupportedFallback,
}: LoadedFileViewerProps) {
  const { addToScene, clearScene, getScene, zoomToObjects } = useScene();

  useEffect(() => {
    handleUpdateScene();
  }, [loadedFile, file?.id]);

  const handleUpdateScene = () => {
    clearScene();
    if (!file) return;
    const loadedScan = getLoadedScan(
      file,
      buildLoadedFiles(file.id, loadedFile)
    );
    if (!loadedScan) return;
    // Clone so multiple viewers can host the same file without stealing the mesh
    const group = centerObjects([loadedScan.clone()]);
    group.name = `file_${file.id}`;
    addToScene([group]);
  };

  const handleMount = () => {
    if (!file) return;
    const group = getScene().getObjectByName(`file_${file.id}`);
    if (group) zoomToObjects([group]);
  };

  const renderMessage = () => {
    return (
      isLoading && (
        <div className={classes.message}>
          Loading file
          <LoopingEllipsis />
        </div>
      )
    );
  };

  const renderViewer = () => {
    if (!file || isLoading) return null;
    const loadedFiles = buildLoadedFiles(file.id, loadedFile);
    if (getLoadedScan(file, loadedFiles)) {
      return (
        <Scene
          className={twMerge(classes.scene, sceneClassName)}
          onMount={handleMount}
        />
      );
    }
    const loadedSequence = getLoadedSweethSequence(
      { id: file.id, fileTypeId: file.fileTypeId ?? null },
      loadedFiles
    );
    if (loadedSequence) {
      return (
        <SweethSequenceViewer className={classes.scene} file={loadedSequence} />
      );
    }
    const loadedImage = getLoadedImage(file, loadedFiles);
    if (loadedImage) {
      return <ImageViewer src={loadedImage} />;
    }
    const loadedPdf = getLoadedPdf(file, loadedFiles);
    if (loadedPdf) {
      return (
        <PdfViewer
          className={classes.pdfViewer}
          controlsClassName={
            floatingHeader ? classes.pdfControlsFloatingHeader : undefined
          }
          documentClassName={
            floatingHeader ? classes.pdfDocumentFloatingHeader : undefined
          }
          src={loadedPdf}
        />
      );
    }
    const loadedVideo = getLoadedVideo(file, loadedFiles);
    if (loadedVideo) {
      return <VideoViewer className={classes.videoViewer} src={loadedVideo} />;
    }
    return (
      <div className={twMerge(classes.message, classes.unsupported)}>
        Online viewing is not currently supported for this file type.
        {unsupportedFallback}
      </div>
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.sceneWrapper}>
        {renderMessage()}
        {renderViewer()}
      </div>
    </div>
  );
}
